"use client";

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import CommonHeader from "@/components/common/CommonHeader";
import { TimeTrackingHistory } from "./TimeTrackingHistory";
import { TimeTrackingReports } from "./TimeTrackingReports";
import { useAuth } from "@/context/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { AlertCircle } from "lucide-react";

export function StaffTimeTrackingPage() {
  const { user, isLoading } = useAuth();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();

  // Redirect non-admins
  useEffect(() => {
    if (!isLoading && !isAdmin) {
      navigate("/");
    }
  }, [isLoading, isAdmin, navigate]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show access denied for non-admins
  if (!isAdmin) {
    return (
      <div className="space-y-8 max-w-[1600px]">
        <CommonHeader
          title="Staff Time Tracking"
          description="View and manage staff working hours"
        />
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <div>
                <h3 className="text-lg font-semibold">Access Denied</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  You must be an administrator to view time tracking history and reports.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-[1600px]">
      <CommonHeader
        title="Staff Time Tracking"
        description="View and manage staff working hours"
      />

      <Tabs defaultValue="history" className="space-y-4">
        <TabsList>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="history" className="space-y-4">
          <TimeTrackingHistory />
        </TabsContent>
        <TabsContent value="reports" className="space-y-4">
          <TimeTrackingReports />
        </TabsContent>
      </Tabs>
    </div>
  );
}

